"use client";

import { useState, useTransition } from "react";
import { useLocale } from "next-intl";
import { usePathname, useRouter } from "@/i18n/navigation";
import { localeMeta, type AppLocale } from "@/i18n/routing";

export function LocaleSwitcher() {
  const locale = useLocale() as AppLocale;
  const pathname = usePathname();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [, startTransition] = useTransition();

  const current = localeMeta[locale];

  const switchTo = (next: AppLocale) => {
    setOpen(false);
    if (next === locale) return;
    startTransition(() => {
      router.replace(pathname, { locale: next });
    });
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={current.label}
        className="flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border-2 border-[var(--zine-ink)] bg-[var(--zine-cream)] px-3 py-1.5 font-zine-label text-[11px] tracking-[0.18em] uppercase"
      >
        <span aria-hidden>{current.flag}</span>
        <span className="hidden sm:inline">{current.native}</span>
        <span aria-hidden className="opacity-60">▾</span>
      </button>
      {open ? (
        <ul className="absolute right-0 z-30 mt-2 min-w-[160px] rounded-2xl border-2 border-[var(--zine-ink)] bg-[var(--zine-cream)] p-1 [box-shadow:3px_3px_0_var(--zine-ink)]">
          {(Object.keys(localeMeta) as AppLocale[]).map((code) => {
            const m = localeMeta[code];
            const active = code === locale;
            return (
              <li key={code}>
                <button
                  type="button"
                  onClick={() => switchTo(code)}
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left font-zine-label text-[12px] tracking-[0.14em] hover:bg-[var(--zine-paper-deep)]"
                  style={{
                    background: active ? "var(--zine-paper-deep)" : "transparent",
                  }}
                >
                  <span aria-hidden>{m.flag}</span>
                  <span>{m.native}</span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
